<template>
  <div class="flex-column gap-18">
    <div>
      <template v-if="loginTitle && loginDescription">
        <h3 class="m-0 mb-2">
          {{ $t(loginTitle) }}
        </h3>
        <p class="mb-2">
          {{ $t(loginDescription) }}
        </p>
      </template>
      <p v-else class="bold has-text-size-14 mb-1">
        {{ $t(loginInputLabel) }}
      </p>
      <PhoneInput
        v-model="phoneLogin.customerPhone"
        @send="submitPhone"
        @foreign="rejectPhone"
      />
    </div>
    <button class="btn btnL mt-1 p-2 has-text-size-14" @click="submitPhone">
      {{ $t(loginButtonToken) }}
    </button>
    <p
      v-if="errorText"
      class="p-caption bold"
      style="color: var(--systemError)"
    >
      {{ $t(errorText, errorParams) }}
    </p>
  </div>
  <div v-if="isOTPPopupOpen" class="otp-backdrop">
    <div class="otp-popup">
      <template v-if="phoneLogin.phoneLoginStatus === 1">
        <div class="flex-column gap-10">
          <div class="flex-row align-items-center gap-18">
            <div class="illustration">
              <IllustrationsShield />
            </div>
            <p class="has-text-size-20 bold">
              {{ $t("bnpl:checkCode") }}
            </p>
          </div>
          <p>
            {{ $t("bnpl:typeCheckCode") }}
          </p>
        </div>
      </template>

      <template v-else-if="phoneLogin.phoneLoginStatus === 2">
        <div class="flex-column gap-10">
          <div class="flex-row align-items-center gap-18">
            <div style="width: 39px; height: 39px">
              <IllustrationsErrorCross />
            </div>
            <div class="tryAgain">
              {{ $t("bnpl:login.fail") }}
            </div>
          </div>
          <p>{{ $t("bnpl:tryAgain") }}</p>
        </div>
      </template>

      <input
        v-model="phoneLogin.checkCodeDigits"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        autofocus
        class="textInput otpInput"
        :style="{
          paddingLeft: phoneLogin.checkCodeDigits
            ? 'calc(14px + 1.5rem)'
            : '14px',
        }"
        @input="inputOtp($event)"
        @paste="inputOtp($event)"
      />

      <button
        id="submitCode"
        ref="submitCheckCodeButton"
        class="btn btnL stretch grow"
        @click="submitCheckCode"
      >
        {{ $t("bnpl:login") }}
      </button>
      <div class="close" @click="closePopup">
        <IconsClose />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useBnplApplicationStore } from "~/stores/bnplapplication";
import { useSessionStore } from "~/stores/session";
import { generateNonce } from "~/utils/util";
import { Track } from "~/utils/gtag";
import { isCountryPhoneConfigured, isLocalPhone } from "~/utils/phone";
import {
  INVALID_PHONE_TEXT,
  loginErrorParams,
  loginErrorText,
} from "~/utils/loginError";
import { useUserStore } from "~/stores/user";
import { useRoute } from "vue-router";
import { useSelfcareStore } from "~/stores/selfcare";
import { BNPL_APPLICATION_STATUS } from "~/utils/const";
import { useOnboardingStore } from "~/stores/onboarding";
import { loadInternalMessages } from "~/utils/i18n";
import { i18nInstance } from "~/plugins/i18n";
import { useProfileStore } from "~/stores/profile";
import { usePrescoreStore } from "~~/stores/prescore";
import { useB2bPrescoreStore } from "~~/stores/b2bPrescore";
import { useB2bFlowStore } from "~~/stores/b2bFlow";
import { useCartStore } from "~~/stores/cart";
import { useCountryStore } from "~~/stores/country";
import { ref } from "vue";
import { useRecaptcha } from "~/composables/useRecaptcha";
import { useWebOtp } from "~/composables/useWebOtp";
import { useConfigStore } from "~/stores/config";
import { getActiveLandingParams } from "~/utils/landingParamUtil";

const route = useRoute();

const props = defineProps({
  loginTitle: {
    type: String,
    required: false,
  },
  loginDescription: {
    type: String,
    required: false,
  },
  loginInputLabel: {
    type: String,
    default: "bnpl:phone",
  },
  loginButtonToken: {
    type: String,
    default: "bnpl:payment.login.button.start",
  },
});

const sessionStore = useSessionStore();
const userStore = useUserStore();
const bnplApplicationStore = useBnplApplicationStore();
const selfcareStore = useSelfcareStore();
const onboardingStore = useOnboardingStore();
const profileStore = useProfileStore();
const prescoreStore = usePrescoreStore();
const b2bPrescoreStore = useB2bPrescoreStore();
const cartStore = useCartStore();
const countryStore = useCountryStore();
const b2bFlowStore = useB2bFlowStore();

const { executeRecaptcha } = useRecaptcha();
const configStore = useConfigStore();

onMounted(async () => {
  await configStore.fetchAutoloadConfigs();
});

let secondFactorId = null;

const errorText = ref(null);
const errorParams = ref({});

// only numbers of the configured country can log in, a number of another country is
// refused instead of being reshaped into one
function rejectPhone() {
  errorText.value = INVALID_PHONE_TEXT;
}

const showErrorToast = ref(false);

const submitCheckCodeButton = ref(null);

const isCheckCodeSubmitted = ref(false);

const isOTPPopupOpen = ref(false);

const countDown = ref(null);

const inputOtp = async (e) => {
  await validateAndSubmitCheckCode();
};

const phoneLogin = reactive({
  customerPhone: userStore.customerPhone,
  phoneLoginStatus: 0,
  checkCodeDigits: null,
  seconds: 30,
});

useWebOtp(async (code) => {
  phoneLogin.checkCodeDigits = code;
  await validateAndSubmitCheckCode();
}, isOTPPopupOpen);

const closePopup = () => {
  isOTPPopupOpen.value = !isOTPPopupOpen.value;
};

function popupErrorToast() {
  showErrorToast.value = true;
  setTimeout(() => {
    showErrorToast.value = false;
  }, 3000);
}

async function submitPhone() {
  if (isCountryPhoneConfigured() && !isLocalPhone(phoneLogin.customerPhone)) {
    rejectPhone();
    return;
  }
  errorText.value = null;

  let nonce = generateNonce();
  sessionStore.nonce = nonce;

  const recaptchaToken = await executeRecaptcha();

  let loginRequest = {
    user: phoneLogin.customerPhone,
    nonce: nonce,
    recaptchaToken,
    bnpl: {
      publicId: route.params.publicId,
      financier: route.params.financier,
    },
  };

  if (route.name === "financier-prescore") {
    if (!cartStore.cart.sum) {
      popupErrorToast();
      return;
    }
  }
  sessionStore.login(loginRequest).then(
    async (result) => {
      if (!!result.bypass) {
        if (route.name === "financier-publicId") {
          sessionStore.savedLocalPosition = result.flowPosition;
          await loadInternalMessages(i18nInstance, countryStore.locale);
          bnplApplicationStore.loadEverything(route).then(
            () => {
              if (
                result.status === BNPL_APPLICATION_STATUS.INITIAL &&
                result.flowPosition !== "INTRO"
              ) {
                onboardingStore.load(countryStore.countryId).then(() => {
                  bnplApplicationStore.bnplApplication.currentPosition =
                    "ONBOARDING";
                });
              } else if (result.status === BNPL_APPLICATION_STATUS.CANCELLED) {
                window.open("/" + route.params.financier, "_self");
              } else {
                bnplApplicationStore.bnplApplication.currentPosition =
                  result.flowPosition;
              }
            },
            () => {
              console.log("Hiba a betöltés közben");
            }
          );
        } else if (route.name === "financier") {
          await loadInternalMessages(i18nInstance, countryStore.locale);
          await profileStore.fetch();
          selfcareStore.navigateTo("dashboard");
        }
      } else {
        secondFactorId = result.secondFactorResponse.secondFactorId;
      }
      phoneLogin.phoneLoginStatus = 1;
      // analytics must never block the login flow
      try {
        Track.event("login");
      } catch (error) {
        console.error("Analytics event failed", error);
      }
      isOTPPopupOpen.value = true;
    },
    (error) => {
      errorText.value = loginErrorText(error);
      errorParams.value = loginErrorParams(error);
      console.log("Login request failed " + error);
      console.error(error);
    }
  );

  clearInterval(countDown.value);
  phoneLogin.seconds = 30;
  createCountDown();
}

async function validateAndSubmitCheckCode() {
  const otpInputValid = /^\d{6}$/.test(phoneLogin.checkCodeDigits);
  if (otpInputValid) {
    await submitCheckCode();
  }
}
async function submitCheckCode() {
  if (isCheckCodeSubmitted.value) {
    return;
  }

  let fullCheckCode = phoneLogin.checkCodeDigits.slice(0, 6);
  let finishLoginRequest = {
    nonce: sessionStore.nonce,
    code: fullCheckCode,
    id: secondFactorId,
    landingParams: getActiveLandingParams(),
  };
  isCheckCodeSubmitted.value = true;

  try {
    await sessionStore.finishLogin(finishLoginRequest).then(
      async (result) => {
        // analytics must never block the login flow
        try {
          Track.event("login_2fa");
          fbq("trackCustom", "IcBnplLogin");
        } catch (error) {
          console.error("Analytics event failed", error);
        }
        if (route.name === "financier-publicId") {
          sessionStore.savedLocalPosition = result.flowPosition;
          await loadInternalMessages(i18nInstance, countryStore.locale);
          bnplApplicationStore.loadEverything(route).then(
            () => {
              if (
                result.status === BNPL_APPLICATION_STATUS.INITIAL &&
                result.flowPosition !== "INTRO"
              ) {
                onboardingStore.load(countryStore.countryId).then(() => {
                  bnplApplicationStore.bnplApplication.currentPosition =
                    "ONBOARDING";
                });
              } else if (result.status === BNPL_APPLICATION_STATUS.CANCELLED) {
                window.open("/" + route.params.financier, "_self");
              } else {
                bnplApplicationStore.bnplApplication.currentPosition =
                  result.flowPosition;
              }
            },
            () => {
              console.log("Hiba a betöltés közben");
            }
          );
        } else if (route.name === "financier") {
          await loadInternalMessages(i18nInstance, countryStore.locale);
          await profileStore.fetch();
          selfcareStore.navigateTo("dashboard");
        } else if (route.name === "financier-prescore") {
          await prescoreStore.createPrescore();
          await bnplApplicationStore.loadEverything(route);
          await profileStore.fetch();
          navigateTo({
            name: "financier-prescore-prescoreId",
            params: { prescoreId: bnplApplicationStore.publicId },
          });
        } else if (route.name === "b2b-financier-prescore") {
          await b2bPrescoreStore.createPrescore();
          await b2bFlowStore.loadEverything(route);
          await profileStore.fetch();
          navigateTo({
            name: "b2b-financier-prescore-prescoreId",
            params: { prescoreId: b2bFlowStore.publicId },
          });
        }
      },
      function (error) {
        phoneLogin.phoneLoginStatus = 2;
        phoneLogin.checkCodeDigits = null;
        isCheckCodeSubmitted.value = false;
        console.log("HIBA" + error);
      }
    );
  } catch (error) {
    phoneLogin.phoneLoginStatus = 2;
    phoneLogin.checkCodeDigits = null;
    isCheckCodeSubmitted.value = false;
    console.log("HIBA" + error);
  }
  phoneLogin.checkCodeDigits = null;
}

function resendCheckCode() {
  submitPhone();
  phoneLogin.seconds = 30;
  clearInterval(countDown.value);
  createCountDown();
}

function createCountDown() {
  countDown.value = setInterval(() => {
    phoneLogin.seconds--;
    if (phoneLogin.seconds === 0) {
      clearInterval(countDown.value);
    }
  }, 1000);
}
</script>

<style scoped lang="scss">
.otp-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.4);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.otp-popup {
  position: relative;
  background: white;
  padding: 32px 24px;
  margin: 8px;
  border-radius: 12px;

  display: flex;
  flex-direction: column;
  gap: 20px;
  width: min-content;
  max-width: 100%;
  box-sizing: border-box;
}

.illustration {
  svg {
    width: 64px;
    height: 64px;
  }
}

@media only screen and (max-width: 792px) {
  .otp-popup {
    padding: 24px;
  }
}

.otpInput {
  width: 280px;
  max-width: 100%;
  text-align: center;
  font-weight: bold;
  letter-spacing: 1.5rem;
}

.close {
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;

  svg {
    display: block;
    margin: 0;
  }
}
</style>
